import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Select, Space, message, Tag } from 'antd';
import { agentApi, agentServiceApi, type AgentDTO, type AgentServiceChannelDTO } from '../api/kfc';

const AgentServiceList: React.FC = () => {
  const [agents, setAgents] = useState<AgentDTO[]>([]);
  const [services, setServices] = useState<AgentServiceChannelDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncingId, setSyncingId] = useState<string | null>(null);
  const [filterAgentIds, setFilterAgentIds] = useState<string[]>([]);
  const [syncAgentId, setSyncAgentId] = useState<string | null>(null);

  const loadAgents = () => {
    agentApi.list().then((res) => setAgents(res.data)).catch(() => setAgents([]));
  };

  const loadServices = (agentId?: string) => {
    setLoading(true);
    agentServiceApi
      .list(agentId)
      .then((res) => setServices(res.data))
      .catch(() => setServices([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAgents();
  }, []);

  useEffect(() => {
    const agentId = filterAgentIds?.length === 1 ? filterAgentIds[0] : undefined;
    loadServices(agentId);
  }, [filterAgentIds]);

  const sync = (agentId: string) => {
    setSyncingId(agentId);
    agentServiceApi
      .syncFromAgent(agentId)
      .then((res) => {
        message.success(res.data.success ? res.data.message : (res.data.message || '同步失败'));
        if (res.data.success) loadServices(filterAgentIds?.length === 1 ? filterAgentIds[0] : undefined);
      })
      .catch((e) => message.error(e.response?.data?.message || '同步失败'))
      .finally(() => setSyncingId(null));
  };

  const filteredServices =
    filterAgentIds?.length > 0
      ? services.filter((s) => s.agentId && filterAgentIds.includes(s.agentId))
      : services;

  return (
    <>
      <Card
        title="代理渠道列表（服务渠道）"
        extra={
          <Space>
            <Select
              mode="multiple"
              placeholder="按代理名称或ID筛选"
              allowClear
              style={{ minWidth: 260 }}
              maxTagCount="responsive"
              value={filterAgentIds}
              onChange={setFilterAgentIds}
              options={agents.map((a) => ({
                value: a.id!,
                label: `${a.basicInfo?.name ?? ''} (${a.basicInfo?.identifier ?? a.id})`,
              }))}
              filterOption={(input, opt) =>
                (opt?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase())
              }
            />
            <Select
              placeholder="选择代理"
              allowClear
              style={{ minWidth: 180 }}
              value={syncAgentId}
              onChange={setSyncAgentId}
              options={agents.map((a) => ({
                value: a.id!,
                label: a.basicInfo?.name ?? a.id,
              }))}
            />
            <Button
              type="primary"
              onClick={() => syncAgentId && sync(syncAgentId)}
              loading={syncingId !== null}
              disabled={!syncAgentId}
            >
              同步渠道
            </Button>
          </Space>
        }
      >
        <p style={{ marginBottom: 16 }}>
          操作区：按代理筛选渠道列表；选择代理后点击「同步渠道」将调用代理的获取服务渠道列表 API 并更新本地数据。
        </p>
        <Table
          loading={loading}
          dataSource={filteredServices}
          rowKey={(r) => r.id ?? `${r.agentId}-${r.serviceId}`}
          scroll={{ x: 800 }}
          columns={[
            { title: '代理ID', dataIndex: 'agentId', width: 120, ellipsis: true },
            { title: '服务ID', dataIndex: 'serviceId', width: 90 },
            { title: '服务渠道名称', dataIndex: 'serviceName', width: 160, ellipsis: true },
            {
              title: '状态',
              dataIndex: 'serviceStatus',
              width: 80,
              render: (v: number) =>
                v === 0 ? <Tag color="green">启用</Tag> : <Tag color="default">禁用</Tag>,
            },
            { title: '国家', dataIndex: 'country', width: 120, ellipsis: true },
            { title: '服务类型', dataIndex: 'stype', width: 120, ellipsis: true },
          ]}
          pagination={{ pageSize: 20 }}
        />
      </Card>
    </>
  );
};

export default AgentServiceList;
